import React from 'react';

import type { RadiusSentence, RegroundRadius } from './api';
import { RADIUS_STYLES } from './constants';

/**
 * Sentence-level source attribution: what each sentence of the report traces back to.
 *
 * The label comes from the payload's own `scale`, indexed by the sentence's `radius`, with `tag`
 * only as a fallback. That is deliberate and measured: in v1 payloads just 30 of 73 sentences carry
 * a `tag` and the sole value is `novel_insight`, which is not in that payload's own scale — reading
 * `tag` first would leave most sentences unlabelled and the rest labelled off-vocabulary. In v2 the
 * two agree by construction, so this shows the tag while also labelling every v1 sentence.
 *
 * Reading each payload with its OWN scale is what keeps stored history honest: v2 renamed the rungs
 * and changed what 1 and 2 mean, so applying the newest names to an older row would silently
 * relabel every sentence in it.
 */

const UNTRACED_RADIUS = '3';

const labelFor = (sentence: RadiusSentence, scale: Record<string, string>): string => {
  if (sentence.radius != null && scale?.[String(sentence.radius)]) {
    return scale[String(sentence.radius)];
  }
  return sentence.tag || (sentence.radius != null ? String(sentence.radius) : 'unscored');
};

const Sentence: React.FC<{ sentence: RadiusSentence; scale: Record<string, string> }> = ({
  sentence, scale,
}) => {
  const level = sentence.radius != null ? String(sentence.radius) : null;
  const untraced = !sentence.backing;

  return (
    <li className="border-l-2 border-gray-100 py-1.5 pl-3">
      <div className="mb-1 flex items-start gap-2">
        <span
          className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
            (level && RADIUS_STYLES[level]) || 'bg-gray-300'}`}
          aria-hidden="true"
        />
        <span className="text-[13px] text-gray-700">{sentence.text}</span>
      </div>
      <div className="flex flex-wrap items-baseline gap-2 pl-4">
        <span className="text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
          {labelFor(sentence, scale)}
        </span>
        {untraced ? (
          <span className={`text-[11.5px] ${
            level === UNTRACED_RADIUS ? 'text-red-600' : 'text-gray-400'}`}>
            traces to nothing a consumer said
          </span>
        ) : (
          <span className="text-[11.5px] italic text-gray-500">{sentence.backing}</span>
        )}
      </div>
    </li>
  );
};

const RegroundSentences: React.FC<{ radius: RegroundRadius | null }> = ({ radius }) => {
  const items = radius?.items;
  if (!radius || !items || Object.keys(items).length === 0) return null;

  const scale = radius.scale ?? {};

  return (
    <details className="border-t border-gray-100 pt-3">
      <summary className="mb-2 cursor-pointer text-[11px] font-bold uppercase tracking-wide
                          text-gray-400">
        Where each sentence comes from ({Object.keys(items).length} blocks)
      </summary>
      <div className="space-y-3">
        {Object.entries(items).map(([path, item]) => {
          const sentences = item?.sentences ?? [];
          if (sentences.length === 0) return null;
          return (
            <div key={path}>
              <code className="mb-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono
                               text-[11.5px] text-gray-700">
                {path}
              </code>
              <ul className="space-y-1">
                {sentences.map((sentence, index) => (
                  <Sentence
                    key={`${path}-${index}`}
                    sentence={sentence}
                    scale={scale}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </details>
  );
};

export default RegroundSentences;
